"use client";

import Link from "next/link";
import { usePrivyAvailable } from "@/components/WalletConnectProvider";

// The actual Privy wallet connection lives in the Blockchains settings tab
// (PoapBlockchainSettings). This prompt only nudges the user there — its
// "Connect wallet" action is a plain link, nothing more.
const BLOCKCHAIN_SETTINGS_HREF = "/dashboard/settings?tab=blockchains";

/**
 * Mirror of AddEmailPrompt for the opposite gap: a signed-in user who has an
 * email but no linked wallet. Without an eligible wallet, WAX rewards are only
 * reserved and POAPs are emailed to them — so this nudges them to connect one
 * by linking to the Blockchains settings tab.
 *
 * `variant` controls presentation: "banner" is a full-width dashboard strip;
 * "inline" is a compact row for embedding next to a settings field.
 */
export function AddWalletPrompt({
  variant = "banner",
}: {
  variant?: "banner" | "inline";
}) {
  const privyAvailable = usePrivyAvailable();
  // Connecting a wallet requires Privy. If it isn't available (insecure origin
  // or unset app id) there's nowhere useful to send the user, so render nothing.
  if (!privyAvailable) return null;

  if (variant === "inline") {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-yellow-700">
        <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>No wallet connected — WAX rewards are reserved, and POAPs are emailed to you</span>
        <Link
          href={BLOCKCHAIN_SETTINGS_HREF}
          className="font-medium underline hover:text-yellow-900"
        >
          Connect wallet
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-md px-4 py-3 mb-6 flex items-center justify-between gap-4">
      <p className="text-sm text-yellow-800">
        Connect a wallet to receive digital assets on-chain. Without one, WAX
        rewards are held as a <strong>reservation</strong> until you connect a
        wallet, and POAPs are emailed to you to finish claiming on POAP.
      </p>
      <Link
        href={BLOCKCHAIN_SETTINGS_HREF}
        className="text-sm font-medium text-yellow-800 underline hover:text-yellow-900 whitespace-nowrap"
      >
        Connect wallet
      </Link>
    </div>
  );
}
